import React, { useRef, useState } from 'react';
import { HiddenSelect, useSelect } from 'react-aria';
import { useSelectState } from 'react-stately';
import ButtonBase from 'components/ButtonBase';
import InputLabel from 'components/InputLabel';
import {
  ListBoxData,
  listBoxDynamicItemRenderer,
  ListBoxItem,
} from 'components/ListBox';
import ListBoxSection from 'components/ListBox/ListBoxSection';
import { listBoxDynamicSectionRenderer } from 'components/ListBox/utils';
import { useMergedRefs } from 'hooks';
import { SELECT_TRIGGER_CLASS_NAME } from './constants';
import SelectMenu from './SelectMenu';
import * as S from './styles';
import { SelectProps } from './types';
import useSelectStateManagement from './useSelectStateManagement';

function SelectComponent<T extends ListBoxData>(
  props: SelectProps<T>,
): React.ReactElement | null {
  const {
    className,
    footer,
    fluid = false,
    isDisabled,
    label,
    name,
    placeholder = 'Select an option',
    startAdornment,
  } = props;

  const triggerRef = useRef<HTMLButtonElement>(null);
  const [triggerEl, setTriggerEl] = useState<HTMLButtonElement>();
  const mergedTriggerRefs = useMergedRefs([triggerRef, setTriggerEl]);

  const stateManagementProps = useSelectStateManagement(props);

  const state = useSelectState({
    ...props,
    ...stateManagementProps,
    children:
      props.children ??
      ((items) => {
        if (items.items?.length) {
          return listBoxDynamicSectionRenderer(items);
        } else {
          return listBoxDynamicItemRenderer(items);
        }
      }),
  });

  const { labelProps, triggerProps, valueProps, menuProps } = useSelect<T>(
    props,
    state,
    triggerRef,
  );

  return (
    <S.SelectRoot className={className} $fluid={fluid}>
      {label && <InputLabel {...labelProps}>{label}</InputLabel>}
      <HiddenSelect {...{ isDisabled, label, name, state, triggerRef }} />
      <S.Trigger
        className={SELECT_TRIGGER_CLASS_NAME}
        endAdornment={
          <S.Adornment aria-hidden="true" position="end">
            <S.Arrow />
          </S.Adornment>
        }
        forwardedAs={ButtonBase}
        inputComponent="span"
        inputComponentProps={{
          children: state.selectedItem
            ? state.selectedItem.rendered
            : placeholder,
          ...valueProps,
        }}
        ref={mergedTriggerRefs}
        startAdornment={startAdornment}
        {...triggerProps}
      />
      <SelectMenu
        footer={footer}
        menuProps={menuProps}
        preventOverflow={{ altAxis: true, mainAxis: false }}
        state={state}
        triggerElement={triggerEl}
      />
    </S.SelectRoot>
  );
}

const Select = Object.assign(SelectComponent, {
  Option: ListBoxItem,
  Section: ListBoxSection,
});

export default Select;
